import Text from '@/components/Text/Text'
import ComboBox from '@/components/forms/combo-box'
import { useMemo } from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { Button, Icon } from '@/components'
import { match, P } from 'ts-pattern'
import UtilityPlanDropdown from 'features/utilities/UtilityPlanDropdown'
import {
  useUtilitySelectionViewModel,
  EligibilityStatus,
} from './useUtilitySelectionViewModel'
import ExternalUtilityConnectionLinks from '@/app/components/ExternalUtilityConnectionLinks/ExternalUtilityConnectionLinks'
import { AlertMessage } from '@/app/components'
import UtilityConnectLinkRow from '@/app/components/UtilityConnectLinkRow/utility-connect-link-row'

export default function UtilitySelection() {
  const viewModel = useUtilitySelectionViewModel()
  const {
    form,
    options,
    initialUtility,
    utilityEligibilityStatus,
    onQueryChange,
    onNextClick,
    onSeeOtherPrograms,
    planRequired,
    planOptions,
    fetchingPlans,
    setSelectedPlanId,
    selectedPlanId,
    planEligibilityStatus,
    isIneligible,
    isSaving,
    utilityProgram,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
    initialSelectedUtility,
    utilityRefId,
    onExternalUtilityConnectButtonClick,
    utilitySearchLoading,
  } = viewModel
  const { register, setValue, getValues } = form
  const utilityBoxIcon = useMemo(
    () =>
      match(utilityEligibilityStatus)
        .with(EligibilityStatus.ELIGIBLE, () => (
          <CheckIcon
            size={22}
            className="rounded-full p-1 bg-themed-green-900 text-themed-white"
          />
        ))
        .with(EligibilityStatus.INELIGIBLE, () => (
          <XIcon
            size={22}
            className="rounded-full p-1 bg-themed-error text-themed-white"
          />
        ))
        .with(EligibilityStatus.NONE, () => null)
        .exhaustive(),
    [utilityEligibilityStatus],
  )

  const utilityEligibilityNotice = match(utilityEligibilityStatus)
    .with(EligibilityStatus.ELIGIBLE, () => (
      <Text variant="body3" className="text-themed-green-900">
        Utility provider is eligible
      </Text>
    ))
    .with(EligibilityStatus.INELIGIBLE, () => (
      <>
        <Text variant="body3" className="text-themed-error">
          Utility provider is not eligible
        </Text>
      </>
    ))
    .with(EligibilityStatus.NONE, () => null)
    .exhaustive()

  const planEligibilityNotice = match(planEligibilityStatus)
    .with(EligibilityStatus.ELIGIBLE, () => (
      <Text variant="body3" className="text-themed-green-900">
        Plan is eligible
      </Text>
    ))
    .with(EligibilityStatus.INELIGIBLE, () => (
      <>
        <Text variant="body3" className="text-themed-error">
          Plan is not eligible
        </Text>
      </>
    ))
    .with(EligibilityStatus.NONE, () => null)
    .exhaustive()

  const buttonDisabled = match({
    utilityEligibilityStatus,
    planRequired,
    planEligibilityStatus,
    isSaving,
    hasExternalUtilityConnectionRequirement,
    hasCompletedExternalUtilityConnectionRequirement,
  })
    .with({ utilityEligibilityStatus: EligibilityStatus.NONE }, () => true)
    .with(
      {
        planRequired: true,
        planEligibilityStatus: EligibilityStatus.NONE,
      },
      () => true,
    )
    .with({ isSaving: true }, () => true)
    .with(
      {
        hasExternalUtilityConnectionRequirement: true,
        hasCompletedExternalUtilityConnectionRequirement: false,
      },
      () => true,
    )
    .otherwise(() => false)

  const buttonText = isIneligible ? 'Back' : 'Next'

  const buttonClick = isIneligible ? onSeeOtherPrograms : onNextClick

  const ctaButton = useMemo(
    () =>
      match({
        hasExternalUtilityConnectionRequirement,
        hasCompletedExternalUtilityConnectionRequirement,
      })
        .with(
          {
            hasExternalUtilityConnectionRequirement: true,
            hasCompletedExternalUtilityConnectionRequirement: false,
          },
          () => null,
        )
        .otherwise(() => (
          <Button
            id="utility-select-next"
            disabled={buttonDisabled}
            onClick={buttonClick}
          >
            {buttonText}
          </Button>
        )),
    [
      hasExternalUtilityConnectionRequirement,
      hasCompletedExternalUtilityConnectionRequirement,
      buttonClick,
    ],
  )

  const headerText = match({
    hasExternalUtilityConnectionRequirement,
  })
    .with(
      { hasExternalUtilityConnectionRequirement: true },
      () => 'Connect your utility account',
    )
    .otherwise(() => 'Who is your utility provider?')

  const subheaderText = match({
    hasExternalUtilityConnectionRequirement,
  })
    .with(
      { hasExternalUtilityConnectionRequirement: true },
      () =>
        'Link your utility account so we can confirm your eligibility and unlock rewards.',
    )
    .otherwise(
      () =>
        'Select your utility provider to integrate your exact electricity rates.',
    )

  return (
    <div className="flex justify-between flex-col gap-8 h-full">
      <div className="flex flex-col gap-6">
        <section className="w-full flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Text variant="h3">{headerText}</Text>
            <Text variant="body2">{subheaderText}</Text>
          </div>
          {hasExternalUtilityConnectionRequirement && (
            <AlertMessage variant="security">
              Your information is encrypted, and you can review permissions
              before you grant access.
            </AlertMessage>
          )}
          {!hasExternalUtilityConnectionRequirement && (
            <div className="flex flex-col gap-2">
              <ComboBox
                initialValue={initialUtility}
                name="utility"
                label={'Search Utilities'}
                options={options}
                setValue={setValue}
                multiple={false}
                getValues={getValues}
                onQueryChange={onQueryChange}
                register={register}
                autoFocus={true}
                required={true}
                icon={utilityBoxIcon}
                optionsLoading={utilitySearchLoading}
                debounceMs={300}
              />
              {utilityEligibilityNotice}
            </div>
          )}
          {Boolean(planOptions.length) &&
            !hasExternalUtilityConnectionRequirement && (
              <>
                <UtilityPlanDropdown
                  planOptions={planOptions}
                  selectedPlanId={selectedPlanId}
                  onChange={(planId) => setSelectedPlanId(Number(planId))}
                  key="plan-dropdown"
                  label="Select Utility Plan"
                  isLoading={fetchingPlans}
                  required={planRequired}
                />
                {planEligibilityNotice}
              </>
            )}
        </section>
        {match({
          hasExternalUtilityConnectionRequirement,
          hasCompletedExternalUtilityConnectionRequirement,
          initialSelectedUtility,
        })
          .with(
            {
              hasExternalUtilityConnectionRequirement: true,
              hasCompletedExternalUtilityConnectionRequirement: false,
            },
            () => (
              <section className="flex flex-col gap-4">
                <Text variant="h3">Eligible providers:</Text>
                <div className="shadow-spread p-4 rounded-2xl">
                  <ExternalUtilityConnectionLinks
                    utilityProgram={utilityProgram}
                    utilityRefId={utilityRefId}
                    onButtonClick={onExternalUtilityConnectButtonClick}
                  />
                </div>

                <div className="shadow-spread flex flex-row p-4 rounded-2xl items-center gap-4">
                  <div className="bg-base-grey-100 rounded-xl flex items-center justify-center p-2">
                    <Icon name="HelpCircle" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text variant="body1">Don’t see your provider?</Text>
                    <Text variant="body4">
                      Check back soon, we add new ones often.
                    </Text>
                  </div>
                </div>
              </section>
            ),
          )
          .with(
            {
              hasCompletedExternalUtilityConnectionRequirement: true,
              initialSelectedUtility: P.nonNullable,
            },
            ({ initialSelectedUtility }) => (
              <div className="shadow-spread p-4 rounded-2xl">
                <UtilityConnectLinkRow
                  utility={initialSelectedUtility}
                  url=""
                  isConnected={true}
                />
              </div>
            ),
          )
          .otherwise(() => null)}
      </div>
      {ctaButton}
    </div>
  )
}
